import React, { useEffect } from 'react';
import clsx from 'clsx';
import { ModalsKeys } from 'global';
import { Box } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { useModal } from 'hooks/useModal';
import { useFormContext } from 'react-hook-form';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLocationActions } from 'hooks/useLocationActions';
import { batch, useDispatch, useSelector } from 'react-redux';
import { FACT_NAMES_NUMERIC } from '@trnsact/business-criteria';
import { useSaleMode } from '../../../context';
import { useSubmissions } from '../../../hooks';
import { DeskingSaleDetailsFormSection } from '../../../shared';
import { deskingActions, deskingSelectors } from '../../../model';
import { defaultCashTerm, displaySettingsForUpdate, equipmentValuesNormalizerToFactValues } from '../../../constants';
import {
  ContactSection,
  EquipmentSection,
  MenuBuilderSection,
  NotesToBuyerSection,
  SendViaSection,
  StructureSection,
} from './sections';
import {
  SaleType,
  PanelProps,
  FormSections,
  BuilderViewType,
  SaleStructureType,
  ProductConfigFactValue,
  FinanceProgramFormValues,
} from '../../../types';

export const PanelForm = (props: PanelProps) => {
  const { onClearState } = useLocationActions();
  const { resetField, getValues, setValue } = useFormContext<FinanceProgramFormValues>();

  const dispatch = useDispatch();

  const isFinanceDetailsEditable = useSelector(deskingSelectors.selectFinanceDetailsEditableStatus);

  useSubmissions({ voDynamicId: props?.vo?.dynamicsVendorOpportunityId ?? '' });
  const { isFinancing, onChangeStructureType, onChangeSaleType } = useSaleMode();
  const { handleOpen } = useModal(ModalsKeys.Confirmation);

  const isBuilderSectionCollapsed = useSelector((state: any) =>
    deskingSelectors.isSectionCollapsed(state, FormSections.MenuBuilder)
  );

  const handleSectionLayout = (section: FormSections, key: 'isOpen' | 'isCollapsed', value: boolean) => {
    dispatch(deskingActions.updateSectionLayout({ section, key, value }));
  };

  const handleClearStructureAndMenuOptions = () => {
    handleOpen({
      title: 'Are you sure you want to reset the proposal builder?',
      message: 'This action will remove all configurations from the Finance Program and Menu.',
      onConfirm: () => {
        onClearState();
        resetField('financeQuote');

        isFinancing && onChangeStructureType(SaleStructureType.Finance);

        batch(() => {
          dispatch(deskingActions.toggleIsEditEquipmentStatus({ status: false }));
          isFinancing && dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.Finance }));
          dispatch(deskingActions.resetStructureAndMenuOptions());
        });
      },
    });
  };

  const handleChangeSelectedProductsConfiguration = (configField: string, nextValue: ProductConfigFactValue) => {
    const normalizerFn = equipmentValuesNormalizerToFactValues[configField];

    dispatch(
      deskingActions.updateAllSelectedProductsConfiguration({
        data: { [configField]: normalizerFn ? normalizerFn(nextValue) : nextValue, customPrice: false },
      })
    );
  };

  const handleSaleTypeChange = (value: SaleType) => {
    const isCash = value === SaleType.Cash;

    if (isCash) {
      const currentMenu = getValues('financeQuote.menu');
      resetField('financeQuote');
      setValue('financeQuote.menu', currentMenu);

      displaySettingsForUpdate.forEach(path => {
        setValue(path, false);
      });

      dispatch(deskingActions.setIsFinanceDetailsEditable({ status: false }));
      dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.AftermarketMenu }));
      dispatch(deskingActions.setMenuTerm({ index: 0, term: defaultCashTerm }));
    } else {
      resetField('displaySettings');
      resetField('financeQuote');

      dispatch(deskingActions.setIsFinanceDetailsEditable({ status: true }));
      dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.Finance }));
      dispatch(deskingActions.resetMenuBuilder());
    }

    onChangeSaleType(value as SaleType.Cash);
  };

  const handleEquipmentRetailPriceChange = (value: number) => {
    handleChangeSelectedProductsConfiguration?.(FACT_NAMES_NUMERIC.DEAL_SIZE, Number(value));
    setValue('equipment.retailPrice', value.toString());
  };

  useEffect(() => {
    if (props.formDefaultValues?.saleDetails?.saleType === SaleType.Cash) {
      handleSaleTypeChange(SaleType.Cash);
    }
  }, []);

  const disableFinanceStructure =
    !!props.configurationByAccount?.actionRestrictions?.disableFinanceStructure || !isFinanceDetailsEditable;

  return (
    <>
      <Box className="row">
        {props.customSections?.[FormSections.Contact]?.(
          handleChangeSelectedProductsConfiguration,
          props?.configurationByAccount
        ) ?? <ContactSection sectionName={FormSections.Contact} onContactChanged={props?.actions?.onContactChanged} />}

        <DeskingSaleDetailsFormSection
          isSectionOpen={true}
          isSectionCollapsed={false}
          value={FormSections.SaleDetails}
          extraAction={props.invoiceAction}
          isSupportSaleDetailsUpdating={!!props.isSupportSaleDetailsUpdating}
          externalDealInfo={props.externalDealInfo}
          onSaleDetailsValuesChange={(key, value) => {
            if (key === 'saleType') handleSaleTypeChange(value as SaleType);
            if (key === 'equipmentSubtotal') handleEquipmentRetailPriceChange(Number(value));
          }}
        />
      </Box>

      {props.customSections?.[FormSections.Equipment]?.(
        handleChangeSelectedProductsConfiguration,
        props?.configurationByAccount
      ) ?? (
        <EquipmentSection
          sectionName={FormSections.Equipment}
          refetchProposalProducts={props.refetchProposalProducts}
          formFieldsRequire={props.configurationByAccount?.formFieldsRequire ?? null}
          handleChangeSelectedProductsConfiguration={handleChangeSelectedProductsConfiguration}
          handleUpdateOpenStatus={(section, nextValue) => {
            handleSectionLayout(section, 'isOpen', nextValue);
          }}
          {...props.actions}
        />
      )}

      <Box className={clsx(isBuilderSectionCollapsed && 'row')}>
        <DndProvider backend={HTML5Backend}>
          <StructureSection
            sectionName={FormSections.Structure}
            createProductSlot={props.createProductSlot}
            formFieldsConfiguration={props.formFieldsConfiguration}
            formDefaultValues={props.formDefaultValues}
            onClearStructureAndMenuOptions={handleClearStructureAndMenuOptions}
            disableFinanceStructure={disableFinanceStructure}
            handleUpdateCollapse={(section, nextValue) => {
              handleSectionLayout(section, 'isCollapsed', nextValue);
            }}
          />

          <MenuBuilderSection
            sectionName={FormSections.MenuBuilder}
            createProductSlot={props.createProductSlot}
            isSupportBehaviorSetting={props?.isSupportBehaviorSetting}
            onClearStructureAndMenuOptions={handleClearStructureAndMenuOptions}
            notification={props.configurationByAccount?.notifications?.menuBuilder}
            handleUpdateCollapse={(section, nextValue) => {
              handleSectionLayout(section, 'isCollapsed', nextValue);
            }}
          />
        </DndProvider>
      </Box>

      <SendViaSection sectionName={FormSections.SendVia} ccEmailOptions={props.ccEmailOptions} />

      <NotesToBuyerSection sectionName={FormSections.NotesToBuyer} />
    </>
  );
};
